import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import type {
  DateValidationError,
  PickerChangeHandlerContext
} from '@mui/x-date-pickers/models';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { Icon } from '@namespace/icons2';
import { useGetThemeConfig } from '@namespace/themes';
import { COLOR_SCHEME } from '../../constants';
import { propsMerge } from '../../utils/propsMerge';
import { AText } from '../AText';
import { Box } from '../Box';
import { AsyncDatePicker } from '../MUI/AsyncDatePicker';
import { formatToDateTime, getSelectedDay } from '../MUI/AsyncDatePicker/utils';
import { FONT_SIZE_INPUT, HEIGHT_INPUT } from './constants';

import styles from './index.module.css';

interface MaterialDatePickerProps {
  name?: string;
  label?: string;
  placeholder?: string;
  date?: Date | DateTime | string | null;
  onChange?: (date: DateTime | null) => void;
  onBlur?: () => void;
  minDate?: Date | DateTime | string;
  maxDate?: Date | DateTime | string;
  error?: boolean;
  format?: string;
  cancelLabel?: string;
  okLabel?: string;
  inputClassName?: string;
  className?: string;
  clearErrors?: (name: string) => void;
  disabled?: boolean;
  iconClassName?: string;
  labelClassName?: string;
  withIcon?: boolean;
  size?: 'xs' | 's' | 'm';
  focus?: boolean;
  success?: boolean;
  open?: boolean;
  disabledSuccess?: boolean;
  labelSize?: 's' | 'm';
  variant?: 'dialog' | 'inline';
  initialFocusedDate?: Date | DateTime | string;
  shouldDisableDate?: (date: DateTime) => boolean;
  openTo?: 'year' | 'month' | 'date';
  views?: Array<'year' | 'month' | 'date'>;
  intentType?: typeof COLOR_SCHEME;
  setError?: (name: string, type: string, message: string) => void;
  minDateMessage?: string;
  maxDateMessage?: string;
  invalidDateMessage?: string;
  dataRole?: string;
  bottomElement?: ReactNode;
}

export const DatePicker: FC<MaterialDatePickerProps> = (props) => {
  const dataPickerConfig = useGetThemeConfig('DataPicker');
  const {
    name = '',
    label = '',
    placeholder = '',
    date = null,
    onChange = () => {},
    onBlur = () => {},
    minDate = new Date('1900-01-01'),
    maxDate = new Date('2100-01-01'),
    error = false,
    format = 'yyyy-MM-dd',
    cancelLabel = 'cancel',
    okLabel = 'ok',
    inputClassName = '',
    className = '',
    clearErrors,
    disabled = false,
    iconClassName = '',
    labelClassName = '',
    withIcon = true,
    size = 's',
    focus = false,
    success = false,
    open = false,
    disabledSuccess = false,
    labelSize = 'm',
    variant = 'dialog',
    initialFocusedDate,
    shouldDisableDate = () => {},
    openTo = 'day',
    views = ['year', 'month', 'day'],
    intentType = COLOR_SCHEME.PRIMARY,
    setError = () => {},
    minDateMessage,
    maxDateMessage,
    invalidDateMessage,
    dataRole,
    bottomElement = null,
    controlOpenExecution,
    ...rest
  } = propsMerge(props, dataPickerConfig);

  const [pickerFocus, setFocus] = useState(focus);
  const [pickerSuccess, setSuccess] = useState(success);
  const [pickerTouched, setTouched] = useState(false);
  const [pickerOpen, setOpen] = useState(open);

  const borderStyles = () => {
    if (pickerFocus) {
      return 'focus';
    }
    if (error) {
      return 'error';
    }
    if (pickerSuccess) {
      return 'default';
    }
    return '';
  };

  useEffect(() => {
    if (!success) {
      if (date && !pickerFocus && !error && pickerTouched && !disabledSuccess) {
        setSuccess(true);
      } else {
        setSuccess(false);
      }
    }
  }, [pickerFocus, error, date, pickerTouched, disabledSuccess, success]);

  const formattedMinDate = formatToDateTime(minDate);
  const formattedMaxDate = formatToDateTime(maxDate);
  const isPlaceholder = placeholder && !date;
  const isDialog = variant === 'dialog';
  const isInline = variant === 'inline';
  const intentTypesSecondary = intentType === COLOR_SCHEME.SECONDARY;
  const inputStyles = useMemo(
    () => ({
      fontSize: size
        ? FONT_SIZE_INPUT[size as keyof typeof FONT_SIZE_INPUT]
        : FONT_SIZE_INPUT.s,
      height: size
        ? HEIGHT_INPUT[size as keyof typeof HEIGHT_INPUT]
        : HEIGHT_INPUT.s,
      ...(intentTypesSecondary
        ? {
            color: isPlaceholder
              ? 'var(--input_02_txt_placeholder)'
              : 'var(--input_02_txt)',
            background: disabled
              ? 'var(--input_02_bg_color_2)'
              : 'var(--input_02_bg_color_1)',
            WebkitTextFillColor: isPlaceholder
              ? 'var(--input_02_txt_placeholder)'
              : 'var(--input_02_txt)'
          }
        : {
            color: isPlaceholder
              ? 'var(--input_txt_placeholder)'
              : 'var(--input_txt)',
            background: disabled
              ? 'var(--input_bg_color_2)'
              : 'var(--input_bg_color_1)',
            WebkitTextFillColor: isPlaceholder
              ? 'var(--input_txt_placeholder)'
              : 'var(--input_txt)'
          })
    }),
    [disabled, intentTypesSecondary, isPlaceholder, size]
  );
  const iconColor = intentTypesSecondary ? 'input_02_txt' : 'input_txt';
  const disabledIconColor = intentTypesSecondary
    ? 'input_02_txt_placeholder'
    : 'input_txt_placeholder';
  const selectedDay = useMemo(
    () => getSelectedDay(date, initialFocusedDate, maxDate),
    [date, initialFocusedDate, maxDate]
  );

  const onCloseHandler = useCallback(() => {
    setOpen(false);
    setFocus(false);
  }, []);

  const onOpenHandler = useCallback(() => {
    if (disabled) {
      return;
    }

    setOpen(true);
    setFocus(true);
  }, [disabled]);

  const onControlOpenHandler = useCallback(() => {
    controlOpenExecution(onOpenHandler);
  }, [controlOpenExecution, onOpenHandler]);

  const onOpen = useCallback(
    () => (controlOpenExecution ? onControlOpenHandler() : onOpenHandler()),
    [controlOpenExecution, onControlOpenHandler, onOpenHandler]
  );

  const onDialogAcceptHandler = useCallback(
    (d: DateTime | null) => {
      setOpen(false);
      setTouched(true);
      setFocus(false);
      onChange(d || selectedDay);
    },
    [selectedDay, onChange]
  );

  const onChangeHandler = useCallback(
    (
      _d: DateTime<boolean> | null,
      e: PickerChangeHandlerContext<DateValidationError>
    ) => {
      if (isInline) {
        onDialogAcceptHandler(_d);
      }
      if (e.validationError) {
        if (e.validationError === 'invalidDate' && invalidDateMessage) {
          setError(invalidDateMessage);
        }
        if (e.validationError === 'minDate' && minDateMessage) {
          setError(minDateMessage);
        }
        if (e.validationError === 'maxDate' && maxDateMessage) {
          setError(maxDateMessage);
        }

        if (typeof e === 'string') {
          setError(e);
        }
      } else if (error) {
        setError(name, undefined);

        if (clearErrors) {
          clearErrors(name);
        }
      }
    },
    [
      clearErrors,
      error,
      invalidDateMessage,
      maxDateMessage,
      minDateMessage,
      name,
      setError
    ]
  );

  const handleRegularIconClick = useCallback(
    () => !disabled && setOpen(true),
    [disabled]
  );

  const handleControlIconClick = useCallback(() => {
    controlOpenExecution(handleRegularIconClick);
  }, [controlOpenExecution, handleRegularIconClick]);

  const handleIconClick = useCallback(
    () =>
      controlOpenExecution
        ? handleControlIconClick()
        : handleRegularIconClick(),
    [controlOpenExecution, handleControlIconClick, handleRegularIconClick]
  );

  return (
    <Box className={className} direction="column">
      {label && (
        <Box
          direction="column"
          bottom="s"
          className={clsx(styles.label, styles[intentType], labelClassName)}
          data-role={dataRole && `${dataRole}-label`}
        >
          <AText size={labelSize} breed="R">
            {label}
          </AText>
        </Box>
      )}

      <Box
        className={`${styles.datePickerWrapper} ${styles[size]}`}
        data-role={dataRole && `${dataRole}-input`}
      >
        <AsyncDatePicker
          {...rest}
          isDialog={isDialog}
          intentType={intentType}
          name={name}
          format={format}
          open={pickerOpen}
          openTo={openTo}
          views={views}
          minDate={formattedMinDate}
          className={inputClassName}
          maxDate={formattedMaxDate}
          value={formatToDateTime(date) || null}
          disabled={disabled}
          variant={variant}
          onClose={onCloseHandler}
          onOpen={onOpen}
          onBlur={onBlur}
          onDialogAccept={onDialogAcceptHandler}
          okLabel={okLabel}
          cancelLabel={cancelLabel}
          placeholder={placeholder}
          referenceDate={selectedDay}
          isInline={isInline}
          slotProps={{
            textField: {
              sx: {
                width: '100%',
                '& .MuiInputBase-root': {
                  backgroundColor: inputStyles.background,
                  height: inputStyles.height,
                  paddingLeft: 0,
                  fontFamily: 'var(--b-font-family-base)',
                  fontSize: inputStyles.fontSize
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                  paddingLeft: 'var(--b-spacing-s)',
                  height: '100%'
                },
                '& .MuiInputBase-input, .MuiInputBase-input.Mui-disabled': {
                  color: inputStyles.color,
                  WebkitTextFillColor: inputStyles.WebkitTextFillColor
                },
                '& .MuiInputBase-root .MuiInputBase-input::placeholder': {
                  opacity: 1
                },
                '& .MuiInputBase-root .MuiInputAdornment-root': {
                  display: 'none'
                }
              }
            }
          }}
          onChange={onChangeHandler}
          closeOnSelect={variant === 'inline'}
          shouldDisableDate={shouldDisableDate}
        />
        {withIcon && (
          <Icon
            onClick={handleIconClick}
            size={size}
            name="icons/general/item/calendar"
            color={isPlaceholder && disabled ? disabledIconColor : iconColor}
            className={clsx(
              styles.icon,
              iconClassName,
              disabled && styles.disabled,
              styles[`s_${size}`]
            )}
          />
        )}
        <Box className={clsx(styles[borderStyles()], styles[intentType])} />
      </Box>
      {bottomElement}
    </Box>
  );
};
